import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSubscriptionStatus } from '../services/subscriptionService';
import { searchRecipesByIngredients } from '../services/api';
import SignedImage from '../components/SignedImage';

const IngredientSearch = () => {
    const navigate = useNavigate();
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searching, setSearching] = useState(false);
    
    // Ingredient input state
    const [ingredientName, setIngredientName] = useState('');
    const [ingredientAmount, setIngredientAmount] = useState('');
    const [ingredients, setIngredients] = useState([]);
    const [showAddSection, setShowAddSection] = useState(true);
    
    // Search options - always include user recipes
    const includeUserRecipes = true;
    
    // Results
    const [exactMatches, setExactMatches] = useState([]);
    const [partialMatches, setPartialMatches] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [error, setError] = useState(null);
    
    // Animation state for cards appearing one by one
    const [visibleCards, setVisibleCards] = useState([]);
    
    // Dragging state for add ingredients modal
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [modalPosition, setModalPosition] = useState({ x: window.innerWidth / 2 - 200, y: 250 }); // Much lower initial position

    useEffect(() => {
        const checkAccess = async () => {
            try {
                const status = await getSubscriptionStatus();
                setSubscription(status);
                
                // Check if user has premium access (unlimited, admin, beta, family only - NOT basic)
                const isPremium = ['unlimited', 'admin', 'beta', 'family'].includes(status.planType);
                
                if (!isPremium) {
                    // Redirect to subscription page if not premium
                    navigate('/profile?tab=subscription');
                    return;
                }

                // Check if we have saved search state to restore
                const savedState = sessionStorage.getItem('ingredientSearchState');
                if (savedState) {
                    try {
                        const parsedState = JSON.parse(savedState);
                        const timeDiff = Date.now() - parsedState.searchTimestamp;
                        
                        // Only restore if the search was recent (within 10 minutes)
                        if (timeDiff < 10 * 60 * 1000) {
                            console.log('🔄 Restoring ingredient search state');
                            setIngredients(parsedState.ingredients || []);
                            setExactMatches(parsedState.exactMatches || []);
                            setPartialMatches(parsedState.partialMatches || []);
                            setHasSearched(parsedState.hasSearched || false);
                            
                            // Clear the saved state so it doesn't restore again
                            sessionStorage.removeItem('ingredientSearchState');
                        }
                    } catch (error) {
                        console.error('Failed to restore search state:', error);
                        sessionStorage.removeItem('ingredientSearchState');
                    }
                }
            } catch (error) {
                console.error('Failed to fetch subscription status:', error);
                setError('Failed to verify access. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        checkAccess();
    }, [navigate]);

    // Auto-search when ingredients change
    useEffect(() => {
        if (ingredients.length > 0) {
            handleSearch();
        } else {
            setExactMatches([]);
            setPartialMatches([]);
            setHasSearched(false);
            setVisibleCards([]);
        }
    }, [ingredients]);

    // Animate cards appearing one by one
    useEffect(() => {
        if (exactMatches.length > 0 || partialMatches.length > 0) {
            setVisibleCards([]);
            
            const allCards = [...exactMatches, ...partialMatches].filter(card => card && card.id);
            
            // Animate cards one by one
            const animateCards = () => {
                let currentIndex = 0;
                const interval = setInterval(() => {
                    if (currentIndex < allCards.length) {
                        setVisibleCards(prev => [...prev, allCards[currentIndex]]);
                        currentIndex++;
                    } else {
                        clearInterval(interval);
                    }
                }, 200); // 200ms delay between each card
            };
            
            animateCards();
        }
    }, [exactMatches, partialMatches]);

    const addIngredient = () => {
        if (!ingredientName.trim()) {
            return;
        }

        const newIngredient = {
            name: ingredientName.trim(),
            amount: ingredientAmount.trim(),
            unit: '' // No separate unit field anymore
        };

        setIngredients([...ingredients, newIngredient]);
        setIngredientName('');
        setIngredientAmount('');
    };

    const removeIngredient = (index) => {
        setIngredients(ingredients.filter((_, i) => i !== index));
    };

    const handleSearch = async () => {
        if (ingredients.length === 0) {
            setExactMatches([]);
            setPartialMatches([]);
            setHasSearched(false);
            setVisibleCards([]);
            return;
        }

        setSearching(true);
        setError(null);
        setHasSearched(true);

        try {
            const results = await searchRecipesByIngredients(ingredients, includeUserRecipes);
            setExactMatches(results.exactMatches || []);
            setPartialMatches(results.partialMatches || []);
        } catch (err) {
            console.error('Search error:', err);
            setError(err.message || 'Failed to search recipes. Please try again.');
        } finally {
            setSearching(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            addIngredient();
        }
    };

    // Dragging functionality for add ingredients modal
    const handleMouseDown = useCallback((e) => {
        // Only drag from title bar
        if (e.target.closest('.modal-title-bar')) {
            setIsDragging(true);
            setDragOffset({
                x: e.clientX - modalPosition.x,
                y: e.clientY - modalPosition.y
            });
        }
    }, [modalPosition]);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging) return;
            
            setModalPosition({
                x: e.clientX - dragOffset.x,
                y: e.clientY - dragOffset.y
            });
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, dragOffset]);

    const formatCookingTime = (minutes) => {
        if (!minutes) return 'No time specified';
        if (minutes < 60) return `${minutes}m`;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-neutral-50 px-0">
                <div className="max-w-4xl mx-auto py-8">
                    <div className="bg-white p-6">
                        <div className="animate-pulse">
                            <div className="h-8 bg-neutral-200 rounded mb-4"></div>
                            <div className="h-4 bg-neutral-200 rounded mb-2"></div>
                            <div className="h-4 bg-neutral-200 rounded w-3/4"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen px-0 relative bg-neutral-50">
            {/* Black border - separate layer so opacity doesn't affect it */}
            <div 
                className="absolute inset-0 pointer-events-none"
                style={{
                    borderLeft: '1px solid #000000',
                    borderRight: '1px solid #000000',
                    borderBottom: '1px solid #000000'
                }}
            />
            
            {/* Dot pattern background - full page */}
            <div 
                className="absolute inset-0 pointer-events-none"
                style={{
                    backgroundImage: 'radial-gradient(circle, #1b263b 1px, transparent 1px)',
                    backgroundSize: '20px 20px',
                    backgroundColor: '#F9F8F6',
                    opacity: 0.15
                }}
            />

            <div className="relative z-10 px-6 py-8">
                {/* Ingredient Cards - Top Left */}
                <div className="mb-8">
                    <div className="flex flex-wrap gap-3">
                        {ingredients.map((ingredient, index) => (
                            <div 
                                key={index}
                                className="bg-white hairline p-2 inline-flex items-center gap-2"
                            >
                                <div className="font-sans text-sm text-neutral-900">
                                    <span className="font-medium">{ingredient.name}</span>
                                    {ingredient.amount && (
                                        <span className="text-neutral-600 ml-2 text-xs">
                                            {ingredient.amount}
                                        </span>
                                    )}
                                </div>
                                <button
                                    onClick={() => removeIngredient(index)}
                                    className="text-neutral-900 hover:opacity-60 transition-opacity font-sans text-sm border-none bg-transparent p-0 m-0 cursor-pointer leading-none"
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                        
                        {/* Add More Ingredients Button - Plus Icon */}
                        {!showAddSection && (
                            <button
                                onClick={() => setShowAddSection(true)}
                                className="bg-white hairline p-2 inline-flex items-center gap-2 text-neutral-900 hover:opacity-60 transition-opacity border-none cursor-pointer"
                                title="Add ingredients"
                            >
                                <span className="font-sans text-sm font-medium">Add</span>
                                <span className="font-sans text-lg leading-none">+</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* Add Ingredients Section - Draggable Popup */}
                {showAddSection && (
                    <div 
                        className="fixed z-50 bg-white w-96 shadow-popup border border-black"
                        style={{
                            left: `${modalPosition.x}px`,
                            top: `${modalPosition.y}px`,
                            cursor: isDragging ? 'grabbing' : 'default'
                        }}
                        onMouseDown={handleMouseDown}
                    >
                        {/* Title Bar */}
                        <div className="modal-title-bar bg-white text-neutral-900 px-4 py-2 flex justify-between items-center cursor-grab active:cursor-grabbing select-none hairline-b">
                            <span className="font-mono uppercase text-sm tracking-wider">
                                Add Ingredients
                            </span>
                            <button
                                onClick={() => setShowAddSection(false)}
                                className="text-neutral-900 text-base flex-shrink-0 border-none bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent hover:text-neutral-500 transition-colors"
                            >
                                ✕
                            </button>
                        </div>
                        
                        {/* Content */}
                        <div className="p-4">
                            
                            <div className="space-y-2">
                                {/* Ingredient and Amount in two columns */}
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block font-mono text-xs uppercase tracking-wider text-neutral-900 mb-1">
                                            Ingredient
                                        </label>
                                        <input
                                            type="text"
                                            value={ingredientName}
                                            onChange={(e) => setIngredientName(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            placeholder="e.g., Flour"
                                            className="w-full px-2 py-1 hairline bg-neutral-50 text-neutral-900 font-sans text-sm focus:outline-none focus:bg-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block font-mono text-xs uppercase tracking-wider text-neutral-900 mb-1">
                                            Amount
                                        </label>
                                        <input
                                            type="text"
                                            value={ingredientAmount}
                                            onChange={(e) => setIngredientAmount(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            placeholder="2 cups"
                                            className="w-full px-2 py-1 hairline bg-neutral-50 text-neutral-900 font-sans text-sm focus:outline-none focus:bg-white"
                                        />
                                    </div>
                                </div>

                                {/* Add Button */}
                                <button
                                    onClick={addIngredient}
                                    className="w-full px-3 py-2 font-mono text-xs uppercase tracking-wider transition-all border-none hover:opacity-60"
                                    style={{ backgroundColor: '#1b263b', color: '#FFFFFF' }}
                                >
                                    Add
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Searching Indicator */}
                {searching && (
                    <div className="max-w-sm mx-auto mb-8">
                        <div className="bg-white hairline p-4 text-center">
                            <div className="font-mono text-sm uppercase tracking-wider text-neutral-600">
                                Searching<span className="animate-pulse">...</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div className="max-w-4xl mx-auto mb-8">
                        <div className="bg-white p-6 hairline">
                            <div className="p-4 bg-red-50 border border-red-200 text-red-800">
                                {error}
                            </div>
                        </div>
                    </div>
                )}

                {/* Horizontal Divider - Full Width */}
                {hasSearched && !searching && visibleCards.length > 0 && (
                    <div className="w-full mb-8">
                        <div className="border-t border-neutral-900"></div>
                    </div>
                )}

                {/* Recipe Cards - Popup Style, Appearing One by One */}
                {hasSearched && !searching && visibleCards.length > 0 && (
                    <div className="max-w-6xl mx-auto">
                        {/* Animated Recipe Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {visibleCards.filter(recipe => recipe && recipe.id).map((recipe, index) => {
                                const isExactMatch = exactMatches.some(exact => exact && exact.id === recipe.id);
                                return (
                                    <div
                                        key={`recipe-${recipe.id}-${index}`}
                                        className="aspect-square bg-neutral-0 hairline transition-all duration-300 cursor-pointer group flex flex-col relative hover:shadow-popup"
                                        style={{
                                            opacity: 0,
                                            animation: 'fadeInUp 0.5s ease-out forwards',
                                            animationDelay: `${index * 200}ms`
                                        }}
                                        onClick={() => {
                                            // Store current search state in sessionStorage
                                            sessionStorage.setItem('ingredientSearchState', JSON.stringify({
                                                ingredients: ingredients,
                                                exactMatches: exactMatches,
                                                partialMatches: partialMatches,
                                                hasSearched: hasSearched,
                                                searchTimestamp: Date.now()
                                            }));
                                            navigate(`/recipes/${recipe.id}`);
                                        }}
                                    >
                                        <div className="flex-1 overflow-hidden">
                                            <SignedImage
                                                src={recipe.images && recipe.images.length > 0 ? recipe.images[0] : null}
                                                alt={recipe.name}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                                category={recipe.category}
                                            />
                                        </div>
                                        <div className="p-3 bg-neutral-0">
                                            <h3 className="font-sans text-sm font-medium mb-1 line-clamp-2" style={{ color: '#1b263b' }}>
                                                {recipe.name}
                                            </h3>
                                            
                                            {/* Matching Ingredients Badges - only for partial matches */}
                                            {!isExactMatch && recipe.matchingIngredients && recipe.matchingIngredients.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mb-2">
                                                    {recipe.matchingIngredients.slice(0, 2).map((ing, idx) => (
                                                        <span
                                                            key={idx}
                                                            className="px-1 py-0 text-xs font-mono uppercase tracking-wider"
                                                            style={{ 
                                                                backgroundColor: '#1b263b', 
                                                                color: '#FFFFFF' 
                                                            }}
                                                        >
                                                            {ing}
                                                        </span>
                                                    ))}
                                                    {recipe.matchingIngredients.length > 2 && (
                                                        <span className="text-xs text-neutral-600">
                                                            +{recipe.matchingIngredients.length - 2}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                            
                                            <div className="flex items-center justify-between text-xs text-neutral-600">
                                                <span className="font-mono uppercase tracking-wider">
                                                    {recipe.category || 'Uncategorized'}
                                                </span>
                                                <span className="font-sans">
                                                    {formatCookingTime(recipe.cooking_time)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* No Results */}
                        {exactMatches.length === 0 && partialMatches.length === 0 && ingredients.length > 0 && (
                            <div className="bg-white p-6 hairline">
                                <div className="text-center py-8">
                                    <h3 className="font-sans text-xl font-medium mb-2" style={{ color: '#1b263b' }}>
                                        No Recipes Found
                                    </h3>
                                    <p className="font-sans text-base text-neutral-600">
                                        Try different ingredients or adjust your search criteria
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default IngredientSearch;